import { createContext, useContext, useEffect, FC, ReactNode } from 'react';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';

import { AuthRole } from '@libs/types';
import { useAuth } from '@carcareers/contexts';
import { adminRoutes, employerRoutes, jobseekerRoutes, noAuthRoutes } from '@carcareers/utils';

export interface GuardProviderProps {
  children: ReactNode;
}

export interface GuardContextType {
  canAccess?: boolean;
}

const GuardContext = createContext<GuardContextType | undefined>(undefined);

export const useGuard = (): GuardContextType => {
  const context = useContext(GuardContext);

  if (!context) {
    throw new Error('useGuard must be used within a GuardProvider');
  }

  return context;
};

export const GuardProvider: FC<GuardProviderProps> = ({ children }) => {
  const value = {};
  const { session } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const getAllowedRoutes = (role: AuthRole) => {
    if (role === AuthRole.ADMIN) {
      const adminRoutesWithPrefix = Object.values(adminRoutes).map(route => 
        route === 'admin' ? route : `admin/${route}`
      );
      return [...adminRoutesWithPrefix, ...Object.values(noAuthRoutes)];
    } else if (role === AuthRole.EMPLOYER) {
      const employerRoutesWithPrefix = Object.values(employerRoutes).map(route => 
        route === 'employer' ? route : `employer/${route}`
      );
      return [...employerRoutesWithPrefix, ...Object.values(noAuthRoutes)];
    } else if (role === AuthRole.JOBSEEKER) {
      const jobseekerRoutesWithPrefix = Object.values(jobseekerRoutes).map(route => 
        route === 'jobseeker' ? route : `jobseeker/${route}`
      );
      return [...jobseekerRoutesWithPrefix, ...Object.values(noAuthRoutes)];
    }
  };

  const getRoleHome = (role: AuthRole) => {
    if (role === AuthRole.ADMIN) {
      return adminRoutes.ADMIN_HOME;
    } else if (role === AuthRole.EMPLOYER) {
      return employerRoutes.EMPLOYER_HOME;
    } else if (role === AuthRole.JOBSEEKER) {
      return jobseekerRoutes.JOBSEEKER_HOME;
    } else {
      return '/';
    }
  };

  useEffect(() => {
    if (session.user?.role) {
      const allowedRoutes = getAllowedRoutes(session.user.role);
      const isAllowed = allowedRoutes?.some((route) => {
        return matchPath({ path: route, end: true }, pathname);
      });

      if (!isAllowed) {
        navigate(getRoleHome(session.user.role));
      }
    }
  }, [session]);

  return <GuardContext.Provider value={value}>{children}</GuardContext.Provider>;
};

export default GuardProvider;
